import { can } from './permissionCore.js'

/**
 * Single source of truth: which permissions a screen needs. Router guard,
 * sidebar/drawer menu, BottomNav, MobileHome and More all call
 * canAccessScreen()/isScreenVisible() — never `can('F..')` directly for
 * navigation.
 *
 * Conditions are strings: 'F1' = F1:X (view), 'F8:G' = create OR update, 'F4:C' = create,
 * 'F8:P' = update, 'F8:D' = delete.
 *   all: every condition must hold.
 *   any: at least one condition must hold (if present).
 * Convention: the action here is the MINIMUM needed to SEE the screen
 * (default X). Write/delete buttons inside a screen keep using
 * canWrite/canDelete.
 *
 * comingSoon: the route still renders PlaceholderView. It stays reachable by
 * URL (permission still applies) but is hidden from every menu / bar / home.
 * Keep in sync with `placeholders` in router/index.js (a test enforces it).
 */
export const SCREENS = {
  Dashboard: {},
  MobileMore: {},
  Drafts: {},
  MobileAlerts: { any: ['F4', 'F17'] },
  PigIntakeList: { all: ['F14'] },
  SowList: { all: ['F1'] },
  SowCardDetail: { all: ['F1'] },
  BoarList: { all: ['F1'] },
  SemenCollectionList: { all: ['F6'] },
  InseminationList: { all: ['F6'] },
  // Needs F1 plus a layout source: F10 (barns/pens) or F4 (feeding board).
  FarmMap: { all: ['F1'], any: ['F10', 'F4'] },
  PenMovementList: { all: ['F1'] },
  SupplierManagement: { all: ['F14'] },
  SaleLotList: { all: ['F4'] },
  SaleOrderList: { all: ['F4'] },
  SowFarrowingTransferList: { all: ['F6'] },
  FarrowingList: { all: ['F6'] },
  FosteringList: { all: ['F6'] },
  UserManagement: { all: ['F8:G'] },
  RoleManagement: { all: ['F8:G'] },
  LifecycleManagement: { all: ['F15'] },
  DailyTaskList: { all: ['F7'] },
  CameraList: { all: ['F3'] },
  ClimateOverview: { all: ['F2'] },
  WallBoard: { any: ['F2', 'F3', 'F7'] },
  ClimateMonitor: { all: ['F2'] },
  ClimateLogList: { all: ['F2'] },
  ClimateThresholdList: { all: ['F2'] },
  ClimateDeviceList: { all: ['F2'] },
  ClimatePLCRegisters: { all: ['F2'] },
  ClimateInfraHealth: { all: ['F2'] },
  ClimateAlerts: { all: ['F2'] },
  // F4:C — chỉ người có quyền TẠO F4 mới chấm cám (che ở giao diện; backend vẫn theo quyền ghi chung F4).
  FeedingLogList: { all: ['F4:C'] },
  VaccineScheduleList: { all: ['F5'] },
  MedicalRecordList: { all: ['F5'] },
  MedicationList: { all: ['F5'] },
  FeedFormulaList: { all: ['F4'] },
  InventoryList: { all: ['F4'] },
  BarnList: { all: ['F10'] },
  PenList: { all: ['F10'] },
  FarmKeyManagement: { all: ['F13:G'] },
  SettingsManagement: { all: ['F10'] },
  WallSettings: { all: ['F10'] },
  NotifySettings: { all: ['F10'] },
  SiteSettingsManagement: { all: ['F8:G'] },
  // --- placeholders (PlaceholderView) ---
  FarmList: { all: ['F8:G'], comingSoon: true },
  PigBatchList: { all: ['F1'], comingSoon: true },
  ReproductiveCycleList: { all: ['F6'], comingSoon: true },
  SalesRecordList: { all: ['F4'], comingSoon: true },
  CameraAlertList: { all: ['F3'] },
  CameraAILogsheet: { all: ['F3'] },
  TaskList: { all: ['F7'], comingSoon: true },
  AlertList: { all: ['F7'], comingSoon: true },
  CleaningScheduleList: { all: ['F11'], comingSoon: true },
  BiosecurityLogList: { all: ['F11'], comingSoon: true },
  SecurityLogList: { all: ['F11'], comingSoon: true },
  AlarmDeviceList: { all: ['F12'], comingSoon: true },
  SoundScheduleConfig: { all: ['F12'], comingSoon: true },
  RedLightConfig: { all: ['F12'], comingSoon: true },
  FCRForecast: { all: ['F9'], comingSoon: true }
}

function check(permissions, cond) {
  const [group, action = 'X'] = cond.split(':')
  return can(permissions, group, action)
}

/** True when `permissions` is enough to open the screen. Unknown names are denied. */
export function canAccessScreen(permissions, routeName) {
  const def = SCREENS[routeName]
  if (!def) return false
  if (def.all && !def.all.every((c) => check(permissions, c))) return false
  if (def.any && !def.any.some((c) => check(permissions, c))) return false
  return true
}

/** Shown in menus / bottom bar / home: accessible AND not a coming-soon placeholder. */
export function isScreenVisible(permissions, routeName) {
  return canAccessScreen(permissions, routeName) && !SCREENS[routeName]?.comingSoon
}

/** First screen name in `names` the user can open, or null. */
export function firstAccessible(permissions, names) {
  return names.find((n) => canAccessScreen(permissions, n)) ?? null
}
